import { stringify } from "query-string"
import { useState } from "react"
import { getProvider } from "@wagmi/core"
import { trim, isAddress, type Hex } from "viem"

import type { ChainId } from "query/graphql"
import { CHAIN_APIS } from "proposal/hooks/useBlockMetadata"
import { getChainIdParams } from "web3/helpers/chainId"
import { fetcher } from "common/helpers/fetcher"
import { jsonParse, jsonStringify } from "common/helpers/serialization"
import { IMPLEMENTATION_SLOTS } from "web3/helpers/proxyStorageSlots"

type ContractData = {
  contractData?: string
  isContractVerified: boolean
}

type Values = {
  data?: ContractData
  isLoading: boolean
  getContractData: (address: string, chainId: ChainId) => Promise<void>
}

type QueryResult = {
  status: string
  message: string
  /**The JSON ABI stringified*/
  result: string
}

export const useContractData = (): Values => {
  const [data, setData] = useState<ContractData>()
  const [isLoading, setIsLoading] = useState(false)

  const getContractData = async (
    address: string,
    chainId: ChainId,
  ): Promise<void> => {
    setIsLoading(true)

    const { reference } = getChainIdParams(chainId)
    const provider = getProvider({ chainId: reference })

    const chain = CHAIN_APIS[reference]

    if (!chain) return

    const { url, apiKey } = chain

    try {
      const addressesToFetch = [address]

      // Check for various proxy implementation patterns
      for await (const slot of IMPLEMENTATION_SLOTS) {
        const proxyStorageHex = await provider?.getStorageAt(address, slot)

        if (proxyStorageHex) {
          const proxyImplementationContractAddress = trim(
            proxyStorageHex as Hex,
          )

          if (isAddress(proxyImplementationContractAddress)) {
            addressesToFetch.push(proxyImplementationContractAddress)
            // If any implementation address is found, no need to check other slots
            break
          }
        }
      }

      const queries = addressesToFetch.map((address) =>
        stringify({
          address,
          action: "getabi",
          module: "contract",
          apikey: apiKey,
        }),
      )

      const resultRequest = await Promise.all(
        queries.map((query) =>
          fetcher.rest<QueryResult>({
            endpoint: `${url}?${query}`,
            error: {
              warning: {
                isSilent: true,
              },
            },
          }),
        ),
      )

      const { result: contractResult, status } = resultRequest[0]
      const proxyContractCall = resultRequest?.[1]

      let isContractVerified = status !== "0"

      if (!isContractVerified) {
        setData({ isContractVerified })
        setIsLoading(false)

        return
      }

      let contractData = jsonParse(contractResult)

      if (proxyContractCall) {
        const { result: proxyResult, status: proxyImplStatus } =
          proxyContractCall
        // Since {address} is a proxy we display only implementation contract ABI
        contractData = jsonParse(proxyResult)
        // Since {address} is a proxy we display verified status of proxy implementation only
        isContractVerified = proxyImplStatus !== "0"
      }

      setData({
        contractData: jsonStringify(contractData),
        isContractVerified,
      })
      setIsLoading(false)
    } catch (error) {
      console.warn(error, "Fetching from Etherscan")
      setIsLoading(false)
    }
  }

  return {
    data,
    isLoading,
    getContractData,
  }
}
